// Detect whether a keydown event would mutate a keyword token (e.g., "AMAP",
// "ALAP") that the cursor is currently inside or selecting. If so, block the
// keystroke and restore the cell to its prior value, parking the caret at the
// end of the token so the restore is visible. Returns
// { wiped: true, newValue, newCursor } when a block should happen, else
// { wiped: false }. (The `wiped` flag name is preserved for back-compat with
// existing call sites; semantically it now means "blocked + restored".)
//
// Token boundary rules:
//   - Caret strictly between first and last keyword char  -> any mutating key blocks
//   - Caret immediately AFTER keyword (right edge)        -> Backspace blocks
//   - Caret immediately BEFORE keyword (left edge)        -> Delete blocks
//   - Selection overlapping any part of a keyword token   -> any mutating key blocks
//
// Mutating keys: single printable character (no Ctrl/Meta), Backspace, Delete.

const isMutatingKey = (event) => {
  if (event.ctrlKey || event.metaKey) return false;
  if (event.key === 'Backspace' || event.key === 'Delete') return true;
  return typeof event.key === 'string' && event.key.length === 1;
};

const findKeywordTokens = (value, keywords) => {
  const tokens = [];
  let cursor = 0;
  value.split(',').forEach((segment) => {
    const segStart = cursor;
    const segEnd = cursor + segment.length;
    const trimmed = segment.trim();
    if (trimmed && keywords.includes(trimmed.toUpperCase())) {
      const innerStart = segStart + segment.indexOf(trimmed);
      const innerEnd = innerStart + trimmed.length;
      tokens.push({ start: innerStart, end: innerEnd });
    }
    cursor = segEnd + 1; // +1 for the comma we split on
  });
  return tokens;
};

const wipeRepKeywordOnMutation = (event, value, selectionStart, selectionEnd, keywords, options = {}) => {
  if (!isMutatingKey(event)) return { wiped: false };
  if (typeof value !== 'string' || value.length === 0) return { wiped: false };

  const tokens = findKeywordTokens(value, keywords);
  if (tokens.length === 0) return { wiped: false };

  const hasSelection = selectionStart !== selectionEnd;
  const matched = tokens.find((tok) => {
    if (hasSelection) {
      // Selection overlaps the token's span at all
      return selectionStart < tok.end && selectionEnd > tok.start;
    }
    if (event.key === 'Backspace') {
      // Caret strictly inside, or at the right edge (immediately after)
      return selectionStart > tok.start && selectionStart <= tok.end;
    }
    if (event.key === 'Delete') {
      // Caret strictly inside, or at the left edge (immediately before)
      return selectionStart >= tok.start && selectionStart < tok.end;
    }
    // Typing a printable char: only strict interior counts as "in the middle"
    return selectionStart > tok.start && selectionStart < tok.end;
  });

  if (!matched) return { wiped: false };

  // Opt-in: allow Backspace to actually delete the entire keyword token rather
  // than block. Used by editable tables where users need a way to remove an
  // AMAP/ALAP keyword without clearing the cell another way.
  if (options.backspaceDeletes && event.key === 'Backspace') {
    const newValue = value.slice(0, matched.start) + value.slice(matched.end);
    return { wiped: true, newValue, newCursor: matched.start };
  }

  return { wiped: true, newValue: value, newCursor: matched.end };
};

export default wipeRepKeywordOnMutation;
